import { catchError, map, Observable, of, switchMap, tap } from 'rxjs';
import { StringLiteralCheck } from '../../util/string-literal-check';
import {
  createActionGroup,
  createFeature,
  createReducer,
  on,
  props,
} from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';

export interface DeleteItemEffectConfig<T> {
  deleteItem: (
    item: T,
    options?: { suppressNotifications?: boolean },
  ) => Observable<void>;
  onSuccess?: (item: T) => void;
  notifyError?: (item: T, error: unknown) => void;
  notifySuccess?: (item: T) => void;
}

export type DeleteItemState<T> =
  | {
      deleting: false;
      itemBeingDeleted: null;
    }
  | {
      deleting: true;
      itemBeingDeleted: T;
    };

/**
 * @param featureName Literal string to be used as the feature name and actions source
 * @returns NGRX feature, actions, and effect builder.
 *
 * The following actions are supported:
 *   delete(item): request deletion of item
 *   deleteSuccess(item): on successful deletion
 *   deleteFail(item): on deletion failure
 *
 * The effect builder is a function returning effects that should be embedded in
 * page's effects.
 */
export function createDeleteItemFeature<
  TActionSource extends string & StringLiteralCheck<TActionSource, 'source'>,
  T,
>(featureName: TActionSource) {
  const actions = createActionGroup({
    source: featureName,
    events: {
      delete: props<{
        item: T;
        options?: { suppressNotifications?: boolean };
      }>(),
      deleteSuccess: props<{
        item: T;
        options?: { suppressNotifications?: boolean };
      }>(),
      deleteFail: props<{
        item: T;
        error: unknown;
        options?: { suppressNotifications?: boolean };
      }>(),
    },
  });
  const feature = createFeature({
    name: featureName,
    reducer: createReducer<DeleteItemState<T>>(
      { deleting: false, itemBeingDeleted: null },
      on(actions.delete, (state, action) => ({
        deleting: true,
        itemBeingDeleted: action.item,
      })),
      on(actions.deleteSuccess, () => ({
        deleting: false,
        itemBeingDeleted: null,
      })),
      on(actions.deleteFail, () => ({
        deleting: false,
        itemBeingDeleted: null,
      })),
    ),
  });
  const createEffects = (
    action$: Actions,
    config: DeleteItemEffectConfig<T>,
  ) => {
    const { deleteItem, onSuccess, notifyError, notifySuccess } = config;
    return {
      delete$: createEffect(() =>
        action$.pipe(
          ofType(actions.delete),
          switchMap(action =>
            deleteItem(action.item).pipe(
              map(() =>
                actions.deleteSuccess({
                  item: action.item,
                  options: action.options,
                }),
              ),
              catchError(error =>
                of(
                  actions.deleteFail({
                    item: action.item,
                    options: action.options,
                    error,
                  }),
                ),
              ),
            ),
          ),
        ),
      ),
      deleteSuccess$: createEffect(
        () =>
          action$.pipe(
            ofType(actions.deleteSuccess),
            tap(action => {
              onSuccess?.(action.item);
              if (!action.options?.suppressNotifications) {
                notifySuccess?.(action.item);
              }
            }),
          ),
        { dispatch: false },
      ),
      deleteFail$: createEffect(
        () =>
          action$.pipe(
            ofType(actions.deleteFail),
            tap(action => {
              console.error('Deletion error', action.error);
              if (!action.options?.suppressNotifications) {
                notifyError?.(action.item, action.error);
              }
            }),
          ),
        { dispatch: false },
      ),
    };
  };
  return { feature, actions, createEffects };
}
